import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getKidSub, useKidToken } from '@/auth/authStore';
import { registeredRoute, registeredKey, setHostedCollector, setHostedTool, StudyToolCollector, isUsageAction, type RegisteredTool, type UsageAction, type UsageMode } from './studyToolTelemetry';

/** Host-only observer. Public demos and other Learn routes never start a collector. */
export function useStudyToolTelemetry() {
  const { pathname, search } = useLocation();
  const token = useKidToken();
  const owner = token ? getKidSub() : null;
  const collector = useRef<StudyToolCollector | null>(null);
  const current = useRef<RegisteredTool | null>(null);
  const year = useRef<number | 'all' | null>(null);
  const mode = useRef<UsageMode | undefined>(undefined);
  mode.current = pathname === '/learn/study/review' ? 'review' : undefined;
  const selected = new URLSearchParams(search).get('year');
  year.current = selected === 'all' ? 'all' : selected && /^[6-9]$|^1[0-2]$/.test(selected) ? Number(selected) : null;
  const onStudy = pathname.startsWith('/learn/study');
  useEffect(() => {
    if (!onStudy || !owner) return;
    const usage = new StudyToolCollector({ contextMode: () => mode.current }); collector.current = usage; setHostedCollector(usage);
    const tick = () => usage.tick();
    const pause = () => usage.tick(true);
    const online = () => { void usage.flush(); };
    const exit = () => { usage.close(); void usage.flush(); };
    const visibility = () => { if (document.visibilityState !== 'visible') pause(); };
    const interaction = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('header,nav,[data-usage-ignore],[data-study-surface]')) return;
      const shared = target.closest('[data-study-tool]')?.getAttribute('data-study-tool');
      if (!shared && !target.closest('main')) return;
      const tool = shared ? registeredKey('study', shared) : current.current?.family === 'subject_lab' && current.current.delivery.startsWith('native') && !document.querySelector('main iframe') ? current.current : null;
      if (!tool) return;
      let action: UsageAction | null = null;
      if (event.type === 'change' && target.matches('input,select,textarea')) action = 'condition_changed';
      const button = target.closest<HTMLButtonElement>('button');
      if (event.type === 'click' && button?.type === 'submit' && button.form) return;
      if (event.type === 'click' && target.closest('button:not(:disabled)')) action = 'control_activated';
      if (event.type === 'pointerup' && target.closest('canvas,svg')) action = 'simulation_manipulated';
      if (current.current?.tool_key === 'formulas' && action === 'control_activated') action = 'formula_selected';
      if (event.type === 'submit') action = 'answer_submitted';
      const explicit = target.closest('[data-study-action]')?.getAttribute('data-study-action') ?? null;
      if (target.closest('[data-study-semantic]')) return;
      if (explicit === 'tutor_submitted' && event.type !== 'submit') return;
      if (action) void usage.interact(tool, year.current, (isUsageAction(explicit) ? explicit : null) ??
        (shared === 'working-canvas' ? 'canvas_edited' : shared === 'pdf-annotation' ? 'annotation_added' : action));
    };
    let scanFrame = 0;
    const scanShared = () => {
      // Animated tools can mutate on every frame. Keep the first queued scan
      // rather than continually postponing readiness behind newer mutations.
      if (scanFrame) return;
      scanFrame = requestAnimationFrame(() => {
        scanFrame = 0;
        if (current.current?.family === 'subject_lab' && current.current.delivery.startsWith('native') && !document.querySelector('main iframe') && document.querySelector('main h1') &&
          document.querySelector('main input:not(:disabled), main select:not(:disabled), main textarea:not(:disabled), main button:not(:disabled), main canvas, main article'))
          void usage.open(current.current, year.current);
        for (const element of document.querySelectorAll('[data-study-tool]')) {
          if (!element.getClientRects().length || element.matches('button')) continue;
          const tool = registeredKey('study', element.getAttribute('data-study-tool') ?? '');
          if (tool) void usage.open(tool, year.current);
        }
      });
    };
    const observer = new MutationObserver(scanShared);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-study-tool'] });
    scanShared();
    const reading = () => { if (current.current) usage.continueReading(current.current); };
    document.addEventListener('scroll', reading, true);
    const interval = setInterval(tick, 30000);
    document.addEventListener('visibilitychange', visibility); window.addEventListener('blur', pause);
    window.addEventListener('online', online); window.addEventListener('pagehide', exit);
    for (const kind of ['change','click','pointerup','submit']) document.addEventListener(kind, interaction, true);
    return () => {
      observer.disconnect(); cancelAnimationFrame(scanFrame); clearInterval(interval); usage.close(); void usage.flush().finally(() => usage.stop());
      collector.current = null; setHostedCollector(null);
      document.removeEventListener('scroll', reading, true);
      document.removeEventListener('visibilitychange', visibility); window.removeEventListener('blur', pause);
      window.removeEventListener('online', online); window.removeEventListener('pagehide', exit);
      for (const kind of ['change','click','pointerup','submit']) document.removeEventListener(kind, interaction, true);
    };
  }, [onStudy, owner]);
  useEffect(() => {
    const usage = collector.current;
    usage?.close();
    current.current = registeredRoute(pathname) ?? null; setHostedTool(current.current);
    if (current.current) usage?.visit(current.current, year.current);
    // Modules signal ready via the existing tool_used bridge, after their async mount succeeds.
    const frame = requestAnimationFrame(() => {
      if (current.current?.family === 'subject_lab' && current.current.delivery.startsWith('native') && !document.querySelector('main iframe') && document.querySelector('main h1') &&
        document.querySelector('main input:not(:disabled), main select:not(:disabled), main textarea:not(:disabled), main button:not(:disabled), main canvas, main article'))
        void usage?.open(current.current, year.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, owner]);
  useEffect(() => { if (current.current) collector.current?.visit(current.current, year.current); }, [pathname, search]);
}
